<div class="min-h-screen bg-gray-50 p-6" *ngIf="!loading && data as detail">
  <div class="mx-auto max-w-7xl space-y-6">
    <div class="bg-white rounded-xl border p-6">
      <h1 class="text-2xl font-bold text-gray-800">Persisted LYFIS Trace Detail</h1>
      <p class="mt-1 text-sm text-gray-500">Snapshot-level GHS and global insights with disease-level DRS, PDH, insights, and trace.</p>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div class="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
        <div class="text-xs uppercase text-indigo-600">GHS</div>
        <div class="text-3xl font-bold text-indigo-700">{{ detail.snapshot.ghs }}</div>
      </div>
      <div class="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
        <div class="text-xs uppercase text-blue-600">GHS Band</div>
        <div class="text-xl font-bold text-blue-700">{{ detail.snapshot.ghsBand || '—' }}</div>
      </div>
      <div class="bg-fuchsia-50 border border-fuchsia-200 rounded-xl p-4 text-center">
        <div class="text-xs uppercase text-fuchsia-600">DRS</div>
        <div class="text-3xl font-bold text-fuchsia-700">{{ detail.disease.drsScore ?? '—' }}</div>
      </div>
      <div class="bg-orange-50 border border-orange-200 rounded-xl p-4 text-center">
        <div class="text-xs uppercase text-orange-600">PDH</div>
        <div class="text-3xl font-bold text-orange-700">{{ detail.disease.pdhScore ?? '—' }}</div>
      </div>
    </div>

    <div class="grid lg:grid-cols-2 gap-6">
      <div class="bg-white rounded-xl border p-6">
        <h2 class="text-lg font-semibold text-gray-800">Snapshot Summary</h2>
        <div class="mt-4 space-y-2 text-sm">
          <div>User ID: <span class="font-mono">{{ detail.snapshot.userId }}</span></div>
          <div>Assessment ID: <span class="font-mono">{{ detail.snapshot.assessmentId }}</span></div>
          <div>Trigger: {{ detail.snapshot.trigger || '—' }}</div>
          <div>Partner ID: {{ detail.snapshot.partnerId || '—' }}</div>
          <div>Global Insights Status: {{ detail.snapshot.globalInsightsGeneratingStatus || '—' }}</div>
        </div>
      </div>
      <div class="bg-white rounded-xl border p-6">
        <h2 class="text-lg font-semibold text-gray-800">Disease Summary</h2>
        <div class="mt-4 space-y-2 text-sm">
          <div>Disease: {{ detail.disease.diseaseName }}</div>
          <div>Disease ID: <span class="font-mono">{{ detail.disease.diseaseId }}</span></div>
          <div>Band: {{ detail.disease.band || '—' }}</div>
          <div>Status: {{ detail.disease.status || '—' }}</div>
          <div>Insights Status: {{ detail.disease.insightsGeneratingStatus || '—' }}</div>
          <div>
            Weights:
            <span class="font-mono"
              >wQ: {{ detail.traceDisplay.scenarioTrace?.weights?.wQ ?? '—' }},
              wL: {{ detail.traceDisplay.scenarioTrace?.weights?.wL ?? '—' }}</span
            >
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl border p-6" *ngIf="detail.traceDisplay.scenarioTrace">
      <h2 class="text-lg font-semibold text-gray-800">Scenario Trace</h2>
      <div class="mt-4 grid md:grid-cols-3 gap-4">
        <div class="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <div class="text-xs uppercase text-blue-600">Selected Scenario</div>
          <div class="mt-2 font-semibold text-blue-800">{{ detail.traceDisplay.scenarioTrace.selectedScenario || '—' }}</div>
        </div>
        <div class="rounded-xl border border-purple-200 bg-purple-50 p-4 text-center">
          <div class="text-xs uppercase text-purple-600">wQ</div>
          <div class="mt-2 text-2xl font-bold text-purple-700">{{ detail.traceDisplay.scenarioTrace.weights.wQ }}</div>
        </div>
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">wL</div>
          <div class="mt-2 text-2xl font-bold text-green-700">{{ detail.traceDisplay.scenarioTrace.weights.wL }}</div>
        </div>
      </div>

      <nz-table
        class="mt-6"
        [nzData]="detail.traceDisplay.scenarioTrace.evaluatedScenarios || []"
        [nzShowPagination]="false"
      >
        <thead>
          <tr>
            <th>Scenario Name</th>
            <th>Condition</th>
            <th>Matched</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of detail.traceDisplay.scenarioTrace.evaluatedScenarios || []">
            <td>{{ row.scenarioName }}</td>
            <td class="font-mono text-xs">{{ row.condition }}</td>
            <td>{{ row.matched ? 'Yes' : 'No' }}</td>
          </tr>
        </tbody>
      </nz-table>
    </div>

    <div class="bg-white rounded-xl border p-6" *ngIf="detail.traceDisplay.totals">
      <h2 class="text-lg font-semibold text-gray-800">Trace Totals</h2>
      <div class="mt-4 grid md:grid-cols-4 gap-4">
        <div class="rounded-xl border border-purple-200 bg-purple-50 p-4 text-center">
          <div class="text-xs uppercase text-purple-600">Questionnaire Raw</div>
          <div class="mt-2 text-2xl font-bold text-purple-700">
            {{ detail.traceDisplay.totals.rawQuestionnairePoints }}
          </div>
        </div>
        <div class="rounded-xl border border-purple-200 bg-purple-50 p-4 text-center">
          <div class="text-xs uppercase text-purple-600">Questionnaire Max</div>
          <div class="mt-2 text-2xl font-bold text-purple-700">
            {{ detail.traceDisplay.totals.maxQuestionnairePoints }}
          </div>
        </div>
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Lab Raw</div>
          <div class="mt-2 text-2xl font-bold text-green-700">
            {{ detail.traceDisplay.totals.rawLabPoints }}
          </div>
        </div>
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Lab Max</div>
          <div class="mt-2 text-2xl font-bold text-green-700">
            {{ detail.traceDisplay.totals.maxLabPoints }}
          </div>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl border p-6">
      <h2 class="text-lg font-semibold text-gray-800">Resolved Questionnaire Inputs</h2>
      <nz-table [nzData]="detail.resolvedEngineInput.questionnaireFacts" [nzShowPagination]="false">
        <thead>
          <tr>
            <th>Fact Key</th>
            <th>Display Value</th>
            <th>Raw Value</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of detail.resolvedEngineInput.questionnaireFacts">
            <td class="font-mono">{{ row.factKey }}</td>
            <td>{{ row.displayValue }}</td>
            <td class="font-mono text-xs">{{ row.rawValue }}</td>
          </tr>
        </tbody>
      </nz-table>
    </div>

    <div class="bg-white rounded-xl border p-6 space-y-6">
      <h2 class="text-lg font-semibold text-gray-800">Questionnaire Breakdown</h2>
      <div class="grid md:grid-cols-3 gap-4">
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Matched Count</div>
          <div class="mt-2 text-2xl font-bold text-green-700">{{ matchedQuestionnaireRows.length }}</div>
        </div>
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Matched Points</div>
          <div class="mt-2 text-2xl font-bold text-green-700">{{ getTotalPoints(matchedQuestionnaireRows) }}</div>
        </div>
        <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
          <div class="text-xs uppercase text-slate-600">Unmatched Count</div>
          <div class="mt-2 text-2xl font-bold text-slate-700">{{ unmatchedQuestionnaireRows.length }}</div>
        </div>
      </div>

      <div>
        <h3 class="text-base font-semibold text-green-700 mb-3">Matched</h3>
        <nz-table [nzData]="matchedQuestionnaireRows" [nzShowPagination]="false">
          <thead>
            <tr>
              <th>Fact</th>
              <th>Display Value</th>
              <th>Raw Value</th>
              <th>Condition</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of matchedQuestionnaireRows">
              <td class="font-mono">{{ row.factKey }}</td>
              <td>{{ row.displayInputValue }}</td>
              <td class="font-mono text-xs">{{ row.rawInputValue }}</td>
              <td class="font-mono text-xs">{{ row.evaluatedCondition }}</td>
              <td>{{ row.pointsAwarded }}</td>
            </tr>
          </tbody>
        </nz-table>
      </div>

      <div>
        <h3 class="text-base font-semibold text-slate-700 mb-3">Unmatched</h3>
        <nz-table [nzData]="unmatchedQuestionnaireRows" [nzShowPagination]="false">
          <thead>
            <tr>
              <th>Fact</th>
              <th>Display Value</th>
              <th>Raw Value</th>
              <th>Condition</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of unmatchedQuestionnaireRows">
              <td class="font-mono">{{ row.factKey }}</td>
              <td>{{ row.displayInputValue }}</td>
              <td class="font-mono text-xs">{{ row.rawInputValue }}</td>
              <td class="font-mono text-xs">{{ row.evaluatedCondition }}</td>
              <td>{{ row.pointsAwarded }}</td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </div>

    <div class="bg-white rounded-xl border p-6 space-y-6">
      <h2 class="text-lg font-semibold text-gray-800">Lab Breakdown</h2>
      <div class="grid md:grid-cols-4 gap-4">
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Matched Count</div>
          <div class="mt-2 text-2xl font-bold text-green-700">{{ matchedLabRows.length }}</div>
        </div>
        <div class="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <div class="text-xs uppercase text-green-600">Matched Points</div>
          <div class="mt-2 text-2xl font-bold text-green-700">{{ getTotalPoints(matchedLabRows) }}</div>
        </div>
        <div class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
          <div class="text-xs uppercase text-amber-600">Available but Unmatched</div>
          <div class="mt-2 text-2xl font-bold text-amber-700">{{ availableButUnmatchedLabRows.length }}</div>
        </div>
        <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
          <div class="text-xs uppercase text-slate-600">Not Available</div>
          <div class="mt-2 text-2xl font-bold text-slate-700">{{ notAvailableLabRows.length }}</div>
        </div>
      </div>

      <div>
        <h3 class="text-base font-semibold text-green-700 mb-3">Matched</h3>
        <nz-table [nzData]="matchedLabRows" [nzShowPagination]="false">
          <thead>
            <tr>
              <th>Marker</th>
              <th>Value</th>
              <th>Condition</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of matchedLabRows">
              <td class="font-mono">{{ row.markerKey }}</td>
              <td>{{ row.inputValue ?? '—' }}</td>
              <td class="font-mono text-xs">{{ row.evaluatedCondition }}</td>
              <td>{{ row.pointsAwarded }}</td>
            </tr>
          </tbody>
        </nz-table>
      </div>

      <div>
        <h3 class="text-base font-semibold text-amber-700 mb-3">Available but Unmatched</h3>
        <nz-table [nzData]="availableButUnmatchedLabRows" [nzShowPagination]="false">
          <thead>
            <tr>
              <th>Marker</th>
              <th>Value</th>
              <th>Condition</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of availableButUnmatchedLabRows">
              <td class="font-mono">{{ row.markerKey }}</td>
              <td>{{ row.inputValue ?? '—' }}</td>
              <td class="font-mono text-xs">{{ row.evaluatedCondition }}</td>
              <td>{{ row.pointsAwarded }}</td>
            </tr>
          </tbody>
        </nz-table>
      </div>

      <div>
        <h3 class="text-base font-semibold text-slate-700 mb-3">Not Available</h3>
        <nz-table [nzData]="notAvailableLabRows" [nzShowPagination]="false">
          <thead>
            <tr>
              <th>Marker</th>
              <th>Value</th>
              <th>Condition</th>
              <th>Points</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of notAvailableLabRows">
              <td class="font-mono">{{ row.markerKey }}</td>
              <td>{{ row.inputValue ?? '—' }}</td>
              <td class="font-mono text-xs">{{ row.evaluatedCondition }}</td>
              <td>{{ row.pointsAwarded }}</td>
            </tr>
          </tbody>
        </nz-table>
      </div>
    </div>

    <div class="grid lg:grid-cols-2 gap-6">
      <div class="bg-white rounded-xl border p-6">
        <h2 class="text-lg font-semibold text-gray-800">Global Insights</h2>
        <div *ngIf="detail.snapshot.globalInsights as insights; else noGlobalInsights" class="mt-4 space-y-4">
          <div class="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
            <div class="text-xs uppercase text-indigo-600">{{ $any(insights).globalScoreCard?.label || 'Global Score' }}</div>
            <div class="mt-2 text-3xl font-bold text-indigo-700">{{ $any(insights).globalScoreCard?.value ?? '—' }}</div>
            <div class="mt-2 inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200">
              {{ $any(insights).globalScoreCard?.status || '—' }}
            </div>
            <p class="mt-3 text-sm text-slate-700">{{ $any(insights).globalScoreCard?.message || '—' }}</p>
          </div>

          <div class="rounded-xl border bg-slate-50 p-4" *ngIf="$any(insights).holisticAnalysis">
            <h3 class="text-base font-semibold text-slate-800">{{ $any(insights).holisticAnalysis?.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-700">{{ $any(insights).holisticAnalysis?.text }}</p>
          </div>

          <div class="space-y-4" *ngIf="$any(insights).sections?.length">
            <div class="rounded-xl border p-4" *ngFor="let section of $any(insights).sections">
              <h3 class="text-base font-semibold text-slate-800">{{ section.title }}</h3>
              <div class="mt-3 space-y-3">
                <div class="rounded-lg bg-slate-50 p-3" *ngFor="let item of section.items">
                  <div class="font-medium text-slate-800">{{ item.title }}</div>
                  <p class="mt-1 text-sm text-slate-600">{{ item.text }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ng-template #noGlobalInsights>
          <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Global insights not available.
          </div>
        </ng-template>
      </div>
      <div class="bg-white rounded-xl border p-6">
        <h2 class="text-lg font-semibold text-gray-800">Disease Insights</h2>
        <div *ngIf="detail.disease.insights as insights; else noDiseaseInsights" class="mt-4 space-y-4">
          <div class="rounded-xl border border-fuchsia-200 bg-fuchsia-50 p-4">
            <div class="text-xs uppercase text-fuchsia-600">{{ $any(insights).scoreCard?.label || 'Disease Score' }}</div>
            <div class="mt-2 inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-fuchsia-700 ring-1 ring-fuchsia-200">
              {{ $any(insights).scoreCard?.status || '—' }}
            </div>
            <p class="mt-3 text-sm text-slate-700">{{ $any(insights).scoreCard?.message || '—' }}</p>
            <div class="mt-2 text-sm font-medium text-fuchsia-700" *ngIf="$any(insights).scoreCard?.ctaText">
              {{ $any(insights).scoreCard?.ctaText }}
            </div>
          </div>

          <div class="rounded-xl border bg-slate-50 p-4" *ngIf="$any(insights).interpretation">
            <div class="text-xs uppercase text-slate-500">Interpretation</div>
            <h3 class="mt-2 text-base font-semibold text-slate-800">{{ $any(insights).interpretation?.title }}</h3>
            <p class="mt-2 text-sm leading-6 text-slate-700">{{ $any(insights).interpretation?.text }}</p>
          </div>

          <div class="rounded-xl border border-amber-200 bg-amber-50 p-4" *ngIf="$any(insights).missingDetails?.length">
            <div class="text-xs uppercase text-amber-600">Missing Details</div>
            <ul class="mt-3 list-disc space-y-2 pl-5 text-sm text-amber-900">
              <li *ngFor="let item of $any(insights).missingDetails">{{ item }}</li>
            </ul>
          </div>

          <div class="space-y-4" *ngIf="$any(insights).sections?.length">
            <div class="rounded-xl border p-4" *ngFor="let section of $any(insights).sections">
              <h3 class="text-base font-semibold text-slate-800">{{ section.title }}</h3>
              <div class="mt-3 space-y-3">
                <div class="rounded-lg bg-slate-50 p-3" *ngFor="let item of section.items">
                  <div class="font-medium text-slate-800">{{ item.title }}</div>
                  <p class="mt-1 text-sm text-slate-600">{{ item.text }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <ng-template #noDiseaseInsights>
          <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            Disease insights not available.
          </div>
        </ng-template>
      </div>
    </div>
  </div>
</div>

<div *ngIf="loading" class="p-10 text-center text-gray-500">Loading trace detail...</div>
